import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tab,
  Tabs,
  Box,
  Chip,
  IconButton,
  Alert,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ListAltIcon from "@mui/icons-material/ListAlt";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ImageIcon from "@mui/icons-material/Image";
import moment from "moment";
import ApiConfig from "../common/config/ApiConfig";
import { useTranslation } from "react-i18next";

const STATUS_COLOR = {
  pending: "warning",
  approve: "success",
  reject: "error",
};
const STATUS_KEY = {
  pending: "topupModal.status.pending",
  approve: "topupModal.status.approve",
  reject: "topupModal.status.reject",
};

function TopupCreditModal({ open, onClose, currency }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [creditInput, setCreditInput] = useState("");
  const [debitInput, setDebitInput] = useState("0");
  const [userId, setUserId] = useState(null);
  const [topupList, setTopupList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editDebitValue, setEditDebitValue] = useState("0");
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [fileEvidence, setFileEvidence] = useState(null);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [rowError, setRowError] = useState({ id: null, message: "" });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewImageSrc, setPreviewImageSrc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const user = localStorage.getItem("user");

  useEffect(() => {
    if (!open) return;
    ApiConfig.post("/profile", { user }).then((res) => {
      if (res.resCode === 200) setUserId(res.data.id);
    });
    fetchList();
  }, [open, user]);

  // <img src> can't attach an Authorization header, and this endpoint is
  // auth-gated, so the file has to be fetched manually and turned into a
  // blob URL rather than referenced directly.
  useEffect(() => {
    if (!previewUrl) {
      setPreviewImageSrc(null);
      return;
    }
    let objectUrl;
    let cancelled = false;
    setPreviewLoading(true);
    fetch(previewUrl, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("Failed"))))
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewImageSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setPreviewImageSrc(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [previewUrl]);

  const fetchList = () => {
    setListLoading(true);
    setRowError({ id: null, message: "" });
    ApiConfig.post("/getmytopup", {}).then((res) => {
      if (res.resCode === 200) setTopupList(res.data);
      setListLoading(false);
    });
  };

  const handleSubmit = () => {
    const credit = parseFloat(creditInput);
    const debit = parseFloat(debitInput);
    if (!credit || credit <= 0) {
      setFormError(t("topupModal.errors.creditRequired"));
      return;
    }
    if (isNaN(debit) || debit < 0) {
      setFormError(t("topupModal.errors.debitRequired"));
      return;
    }
    if (!userId) {
      setFormError(t("topupModal.errors.userNotFound"));
      return;
    }
    setFormError("");
    setFormSuccess("");
    setSubmitting(true);
    const formData = new FormData();
    formData.append("requestBy", userId);
    formData.append("credit", credit);
    formData.append("debit", debit);
    if (fileEvidence) formData.append("fileEvidence", fileEvidence);
    fetch(`${process.env.REACT_APP_API_KEY}/addmembertopup`, {
      method: "POST",
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      body: formData,
    })
      .then((r) => r.json())
      .then((res) => {
        setSubmitting(false);
        if (res.resCode === 200) {
          setCreditInput("");
          setDebitInput("0");
          setFileEvidence(null);
          setFormSuccess(t("topupModal.messages.submitSuccess"));
          fetchList();
          setTab(1);
        } else {
          setFormError(res.message ?? t("topupModal.errors.generic"));
        }
      })
      .catch(() => {
        setSubmitting(false);
        setFormError(t("topupModal.errors.generic"));
      });
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setEditValue(parseFloat(row.credit).toString());
    setEditDebitValue(parseFloat(row.debit).toString());
    setRowError({ id: null, message: "" });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue("");
    setEditDebitValue("0");
    setRowError({ id: null, message: "" });
  };

  const handleSaveEdit = (id) => {
    const credit = parseFloat(editValue);
    const debit = parseFloat(editDebitValue);
    if (!credit || credit <= 0) return;
    if (isNaN(debit) || debit < 0) return;
    setSavingId(id);
    ApiConfig.post("/updatemembertopup", { id, credit, debit }).then((res) => {
      setSavingId(null);
      if (res.resCode === 200) {
        setEditingId(null);
        setEditValue("");
        setEditDebitValue("0");
        fetchList();
      } else {
        setRowError({
          id,
          message: res.message ?? t("topupModal.errors.editFailed"),
        });
      }
    });
  };

  const handleDeleteConfirmed = () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    setDeletingId(id);
    ApiConfig.post("/deletemembertopup", { id }).then((res) => {
      setDeletingId(null);
      if (res.resCode === 200) {
        fetchList();
      } else {
        setRowError({
          id,
          message: res.message ?? t("topupModal.errors.deleteFailed"),
        });
      }
    });
  };

  const userTopups = topupList.filter((t) => t.requestBy?.Username === user);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle className="bg-gradient-to-r from-brand-blue-800 via-brand-blue-700 to-brand-blue-800 border-b border-brand-gold-300/40">
        <AddCircleIcon className="text-brand-gold-300" />
        <span className="text-brand-gold-200 ml-2 font-bold">
          {t("topupModal.title")}
        </span>
      </DialogTitle>

      <Box className="bg-brand-blue-50">
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            "& .MuiTab-root.Mui-selected": { color: "#0d4bb3" },
            "& .MuiTabs-indicator": { backgroundColor: "#d9a520" },
          }}
        >
          <Tab
            icon={<AddCircleIcon />}
            iconPosition="start"
            label={t("topupModal.title")}
          />
          <Tab
            icon={<ListAltIcon />}
            iconPosition="start"
            label={t("topupModal.historyTab")}
          />
        </Tabs>
      </Box>

      <DialogContent dividers>
        {tab === 0 && (
          <div className="flex flex-col space-y-4 p-2">
            <span className="text-brand-blue-800 font-bold text-base">
              {t("topupModal.amountPrompt")}
            </span>
            {formError && <Alert severity="error">{formError}</Alert>}
            {formSuccess && <Alert severity="success">{formSuccess}</Alert>}
            <TextField
              label={`${t("topupModal.amountLabel")}${currency ? ` (${currency.code})` : ""}`}
              type="number"
              value={creditInput}
              onChange={(e) => {
                setCreditInput(e.target.value);
                setFormError("");
                setFormSuccess("");
              }}
              fullWidth
              inputProps={{ min: 1, step: "any" }}
              variant="outlined"
            />
            <div className="flex flex-col space-y-1">
              <span className="text-gray-600 text-sm font-medium">
                {t("topupModal.evidenceLabel")}
              </span>
              <label className="flex items-center justify-center w-full h-[120px] border-2 border-dashed border-brand-blue-300 rounded cursor-pointer bg-brand-blue-50 hover:bg-brand-blue-100 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    setFileEvidence(e.target.files[0] ?? null);
                    setFormError("");
                  }}
                />
                {fileEvidence ? (
                  <div className="flex flex-col items-center space-y-1 p-2">
                    <img
                      src={URL.createObjectURL(fileEvidence)}
                      alt="preview"
                      className="h-[80px] object-contain rounded"
                    />
                    <span className="text-xs text-gray-400">
                      {(fileEvidence.size / 1024).toFixed(0)} KB
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-brand-blue-400">
                    <span className="text-3xl">📎</span>
                    <span className="text-sm">
                      {t("topupModal.uploadPrompt")}
                    </span>
                    <span className="text-xs text-gray-400">
                      {t("topupModal.uploadHint")}
                    </span>
                  </div>
                )}
              </label>
              {fileEvidence && (
                <button
                  className="text-xs text-red-500 text-left hover:underline w-fit"
                  onClick={() => setFileEvidence(null)}
                >
                  {t("topupModal.removeFile")}
                </button>
              )}
            </div>
            <Button
              variant="contained"
              disabled={!creditInput || submitting || !userId}
              onClick={handleSubmit}
              startIcon={<AddCircleIcon />}
              sx={{
                background:
                  "linear-gradient(180deg, #ffd76b, #f4c430 47%, #d9a520)",
                color: "#3c2a06",
                fontWeight: 700,
                border: "2px solid #ffe9a3",
                boxShadow: "0 8px 18px rgba(138,98,8,.28)",
                "&:hover": {
                  background:
                    "linear-gradient(180deg, #ffe9a3, #f4c430 47%, #b3830f)",
                },
                "&.Mui-disabled": {
                  background: "#e5e7eb",
                  color: "#9ca3af",
                  border: "2px solid #e5e7eb",
                },
              }}
            >
              {submitting
                ? t("topupModal.submitting")
                : t("topupModal.submitButton")}
            </Button>
          </div>
        )}

        {tab === 1 && (
          <div className="flex flex-col space-y-2">
            <div className="flex justify-end">
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={fetchList}
                disabled={listLoading}
              >
                {t("topupModal.refresh")}
              </Button>
            </div>
            {listLoading ? (
              <span className="text-center text-gray-500 py-6">
                {t("topupModal.loading")}
              </span>
            ) : userTopups.length === 0 ? (
              <span className="text-center text-gray-400 py-6">
                {t("topupModal.empty")}
              </span>
            ) : (
              <>
                <div className="hidden md:block">
                  <table className="table-auto w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-brand-blue-700 to-brand-blue-800 text-brand-gold-200">
                        <th className="px-3 py-2 text-left">
                          {t("topupModal.table.date")}
                        </th>
                        <th className="px-3 py-2 text-right">
                          {t("topupModal.table.credit")}
                        </th>
                        <th className="px-3 py-2 text-center">
                          {t("topupModal.table.status")}
                        </th>
                        <th className="px-3 py-2 text-left">
                          {t("topupModal.table.approver")}
                        </th>
                        <th className="px-3 py-2 text-center">
                          {t("topupModal.evidence")}
                        </th>
                        <th className="px-3 py-2 text-center">
                          {t("topupModal.table.actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {userTopups.map((row, i) => (
                        <React.Fragment key={row.id}>
                          <tr
                            className={i % 2 === 0 ? "bg-white" : "bg-brand-blue-50"}
                          >
                            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                              {moment(row.createAt).format("DD/MM/YY HH:mm")}
                            </td>
                            <td className="px-3 py-2 text-right font-bold text-green-700">
                              {editingId === row.id ? (
                                <TextField
                                  type="number"
                                  value={editValue}
                                  onChange={(e) =>
                                    setEditValue(e.target.value)
                                  }
                                  size="small"
                                  inputProps={{ min: 1, step: "any" }}
                                  sx={{ width: 100 }}
                                />
                              ) : (
                                <>
                                  +
                                  {parseFloat(row.credit).toLocaleString(
                                    "th-TH",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    },
                                  )}{" "}
                                  {currency?.symbol}
                                </>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Chip
                                label={
                                  STATUS_KEY[row.status]
                                    ? t(STATUS_KEY[row.status])
                                    : row.status
                                }
                                color={STATUS_COLOR[row.status] ?? "default"}
                                size="small"
                              />
                            </td>
                            <td className="px-3 py-2 text-gray-500 text-xs">
                              {row.approveBy?.Username ?? "-"}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {row.fileEvidenceUrl ? (
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() =>
                                    setPreviewUrl(row.fileEvidenceUrl)
                                  }
                                >
                                  <ImageIcon fontSize="small" />
                                </IconButton>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {row.status === "pending" &&
                                (editingId === row.id ? (
                                  <div className="flex justify-center space-x-1">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      disabled={savingId === row.id}
                                      onClick={() => handleSaveEdit(row.id)}
                                    >
                                      <SaveIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={handleCancelEdit}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </div>
                                ) : (
                                  <div className="flex justify-center space-x-1">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleEdit(row)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={deletingId === row.id}
                                      onClick={() =>
                                        setConfirmDeleteId(row.id)
                                      }
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </div>
                                ))}
                            </td>
                          </tr>
                          {rowError.id === row.id && (
                            <tr>
                              <td colSpan={7} className="px-3 pb-2">
                                <Alert
                                  severity="error"
                                  onClose={() =>
                                    setRowError({ id: null, message: "" })
                                  }
                                >
                                  {rowError.message}
                                </Alert>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="md:hidden flex flex-col space-y-2">
                  {userTopups.map((row) => (
                    <div
                      key={row.id}
                      className="rounded-lg border border-brand-blue-100 shadow-sm overflow-hidden bg-white"
                    >
                      <div className="flex items-center justify-between px-3 py-2 bg-brand-blue-50">
                        <span className="text-xs text-gray-500">
                          {moment(row.createAt).format("DD/MM/YY HH:mm")}
                        </span>
                        <Chip
                          label={
                            STATUS_KEY[row.status]
                              ? t(STATUS_KEY[row.status])
                              : row.status
                          }
                          color={STATUS_COLOR[row.status] ?? "default"}
                          size="small"
                        />
                      </div>
                      <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100">
                        <span className="text-gray-500 text-xs">
                          {t("topupModal.table.credit")}
                        </span>
                        {editingId === row.id ? (
                          <TextField
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            size="small"
                            inputProps={{ min: 1, step: "any" }}
                            sx={{ width: 100 }}
                          />
                        ) : (
                          <span className="font-bold text-green-700">
                            +
                            {parseFloat(row.credit).toLocaleString("th-TH", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{" "}
                            {currency?.symbol}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100">
                        <span className="text-gray-500 text-xs">
                          {t("topupModal.table.approver")}
                        </span>
                        <span className="text-xs text-gray-700">
                          {row.approveBy?.Username ?? "-"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100">
                        <span className="text-gray-500 text-xs">
                          {t("topupModal.evidence")}
                        </span>
                        {row.fileEvidenceUrl ? (
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => setPreviewUrl(row.fileEvidenceUrl)}
                          >
                            <ImageIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </div>
                      {row.status === "pending" && (
                        <div className="flex justify-end space-x-1 px-3 py-2 border-t border-gray-100 bg-gray-50">
                          {editingId === row.id ? (
                            <>
                              <IconButton
                                size="small"
                                color="success"
                                disabled={savingId === row.id}
                                onClick={() => handleSaveEdit(row.id)}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEdit(row)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={deletingId === row.id}
                                onClick={() => setConfirmDeleteId(row.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </div>
                      )}
                      {rowError.id === row.id && (
                        <div className="px-3 pb-2 pt-1">
                          <Alert
                            severity="error"
                            onClose={() =>
                              setRowError({ id: null, message: "" })
                            }
                          >
                            {rowError.message}
                          </Alert>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ color: "#0d4bb3" }}>
          {t("topupModal.close")}
        </Button>
      </DialogActions>

      <Dialog
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className="bg-red-600">
          <DeleteIcon className="text-white" />
          <span className="text-white ml-2 font-bold">
            {t("topupModal.confirmDelete.title")}
          </span>
        </DialogTitle>
        <DialogContent dividers>
          <span className="text-gray-700">
            {t("topupModal.confirmDelete.body")}
          </span>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)} sx={{ color: "#0d4bb3" }}>
            {t("topupModal.confirmDelete.cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirmed}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            {t("topupModal.confirmDelete.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!previewUrl}
        onClose={() => setPreviewUrl(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="bg-gradient-to-r from-brand-blue-800 via-brand-blue-700 to-brand-blue-800 border-b border-brand-gold-300/40">
          <ImageIcon className="text-brand-gold-300" />
          <span className="text-brand-gold-200 ml-2 font-bold">
            {t("topupModal.evidencePreviewTitle")}
          </span>
        </DialogTitle>
        <DialogContent dividers className="flex justify-center p-2">
          {previewLoading ? (
            <span className="text-gray-500 py-6">
              {t("topupModal.loading")}
            </span>
          ) : (
            previewImageSrc && (
              <img
                src={previewImageSrc}
                alt="evidence"
                className="max-w-full max-h-[70vh] object-contain rounded"
              />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewUrl(null)} sx={{ color: "#0d4bb3" }}>
            {t("topupModal.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default TopupCreditModal;
